// src/components/UnifiedSurveyModal.jsx
// ============================================================
// REFACTOR: Add EA context banner + improved exit semantics
// Keeps users grounded in EA journey context
// CLIENT ID: Accepts clientId as prop, no hardcoded default
// ============================================================
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import UnifiedSurvey from './UnifiedSurvey';
import toast from 'react-hot-toast';

// Renamed from SURVEY_TITLES to MODULE_TITLES
const MODULE_TITLES = {
  strategy: 'Strategic Priorities',
  capabilities: 'Data & Analytics Capabilities', 
  readiness: 'Data Readiness & Governance'
};

const UnifiedSurveyModal = ({
  isOpen,
  onClose,
  clientId = null,
  userId = 'abc123',
  userProfile = null,
  surveyType = 'strategy',
  onComplete,
  onSaveAndExit,
}) => {
  const modalRef = useRef(null);
  const [lastCompletedSection, setLastCompletedSection] = useState(null);
  
  const effectiveUserProfile = userProfile || {
    client_id: clientId,
    user_id: userId
  };

  // Focus trap
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;
    
    const focusableElements = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;
      
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };
    
    document.addEventListener('keydown', handleTabKey);
    firstElement?.focus();
    
    return () => {
      document.removeEventListener('keydown', handleTabKey);
    };
  }, [isOpen]);

  // ESC key handler
  useEffect(() => {
    if (!isOpen) return;
    
    const onEsc = (e) => {
      if (e.key === 'Escape') {
        handleSaveAndExit();
      }
    };
    
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [isOpen, onClose]);

  const handleBackdrop = (e) => { 
    if (e.target === e.currentTarget) {
      handleSaveAndExit();
    }
  };

  const handleComplete = (data) => {
    // Show completion toast with module-specific messaging
    const moduleTitle = MODULE_TITLES[surveyType] || surveyType;
    toast.success(`${moduleTitle} applied — opportunity rankings updated.`, {
      duration: 4000,
      icon: '✓',
      style: {
        background: '#18365E',
        color: '#fff',
        fontWeight: 500,
      },
    });
    onComplete?.(data);
    onClose?.();
  };

  // REFACTOR D: Updated exit semantics
  const handleSaveAndExit = (data) => {
    // Show save confirmation toast
    toast.success('Progress saved. Rankings update immediately.', {
      duration: 3000,
      icon: '💾',
      style: {
        background: '#18365E',
        color: '#fff',
        fontWeight: 500,
      },
    });
    onSaveAndExit?.(data);
    onClose?.();
  };

  // Handler for section completion (called from UnifiedSurvey)
  const handleSectionComplete = (sectionTitle) => {
    setLastCompletedSection(sectionTitle);
    // Show inline confirmation for partial completion
    toast.success(`${sectionTitle} inputs saved — rankings updated.`, {
      duration: 2500,
      style: {
        background: '#f0fdf4',
        color: '#166534',
        border: '1px solid #bbf7d0',
      },
    });
  };

  if (!isOpen) return null;

  // Warn if clientId is missing
  if (!clientId) {
    console.warn('UnifiedSurveyModal: clientId prop is required');
  }

  let root = document.getElementById('survey-modal-root');
  if (!root) {
    root = document.createElement('div');
    root.id = 'survey-modal-root';
    document.body.appendChild(root);
  }

  const moduleTitle = MODULE_TITLES[surveyType] || surveyType;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${moduleTitle} Module`}
      onClick={handleBackdrop}
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4"
    >
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-[1400px] h-[90vh] overflow-hidden shadow-2xl flex flex-col"
      >
        {/* Modal Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-[#E5E7EB] bg-white shrink-0">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-[#18365E] font-sans">
              {moduleTitle}
            </h2>
          </div>
          
          {/* Auto-save indicator will be passed from UnifiedSurvey */}
          <div id="save-indicator-container" className="flex items-center gap-4">
            <button
              aria-label="Save and return to Personalize"
              onClick={() => handleSaveAndExit()}
              className="w-9 h-9 rounded-lg bg-[#F5F7FA] hover:bg-[#E5E7EB] transition-all duration-200 flex items-center justify-center text-[#717182] hover:text-[#18365E]"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* ============================================================
            REFACTOR B: EA Context Banner
            Keeps users grounded in the EA journey context
            ============================================================ */}
        <div className="px-6 py-3 border-b shrink-0" style={{ backgroundColor: '#F5F7FA', borderColor: '#E5E7EB' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Breadcrumb */}
              <span className="text-xs font-medium" style={{ color: '#717182' }}>Personalization</span>
              <svg className="w-3 h-3" style={{ color: '#CBD5E1' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-xs font-semibold" style={{ color: '#18365E' }}>Surveys</span>
            </div>
            {/* Value proposition */}
            <p className="text-xs italic" style={{ color: '#717182' }}>
              These inputs immediately influence opportunity ranking and ROI assumptions.
            </p>
          </div>
        </div>

        {/* Survey Content */}
        <div className="flex-1 overflow-hidden bg-white">
          <UnifiedSurvey
            isOpen={isOpen}
            onClose={onClose}
            userProfile={effectiveUserProfile}
            clientId={clientId}
            userId={userId}
            surveyType={surveyType}
            onComplete={handleComplete}
            onSaveAndExit={handleSaveAndExit}
            onSectionComplete={handleSectionComplete}
          />
        </div>
      </div>
    </div>,
    root
  );
};

export default UnifiedSurveyModal;